import React from 'react';
import { styled } from '@compiled/react';
import Avatar from '@atlaskit/avatar';
import { Inline } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import Link from '../../common/ui/link';

type Props = {
	/** The tag name to be displayed to users for the item */
	name: string;
	/** URL to direct users to when they click on the item */
	href: string;
	/** Optional icon to display next to the item */
	icon?: {
		url?: string;
	};
};

/**
 * A simple Link item.
 * It's recommended to use the LinkItemPopupContentWrapper with this item component.
 */
export const LinkItem = ({ name, href, icon }: Props) => (
	<ItemWrapper hasIcon={Boolean(icon)}>
		{icon && (
			<Avatar
				appearance="square"
				size="xsmall"
				src={icon.url}
				borderColor="transparent"
				testId="list-with-popup.ui.link-item.avatar"
			/>
		)}
		<TruncatedLink href={href} wordBreak={false}>
			{name}
		</TruncatedLink>
	</ItemWrapper>
);

export const LinkItemPopupContentWrapper = ({ children }: { children: React.ReactNode }) => (
	<Inline space="space.100" shouldWrap>
		{children}
	</Inline>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TruncatedLink = styled(Link)(
	{
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		lineHeight: 1,
		// inline-block is needed to truncate with ellipses
		display: 'inline-block',
		// needed because of display: inline-block
		verticalAlign: 'middle',
		padding: `${token('space.050', '4px')} 0`,
		whiteSpace: 'nowrap',
		'&:focus-within': {
			outline: 'none',
		},
	},
	() =>
		ff('nin-resizable-columns_8z4eo')
			? {
					boxSizing: 'border-box',
					maxWidth: '100%',
				}
			: {
					maxWidth: '200px',
				},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemWrapper = styled.span<{ hasIcon: boolean }>(
	({ hasIcon }) =>
		hasIcon && {
			marginLeft: token('space.negative.025', '-2px'),
			display: 'inline-flex',
			alignItems: 'center',
		},
	{
		'&:focus-within': {
			borderRadius: '3px',
			boxShadow: `0 0 0 2px ${token('color.border.focused', colors.B200)}`,
		},
	},
	() =>
		ff('nin-resizable-columns_8z4eo') && {
			overflow: 'hidden',
		},
);
